'use client';

import { datadogRum } from '@datadog/browser-rum';
import type { LDEvaluationDetail } from 'launchdarkly-js-client-sdk';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';

import { CenteredContainer } from '@/styles/shared';
import { AppConfig } from '@/utils/AppConfig';

import SimpleLoader from '../Loader/SimpleLoader';

type LDProviderProps = {
  children: ReactNode;
};
const LAUNCHDARKLY_CLIENT_ID: string | undefined =
  AppConfig.launchDarklyClientId;

export default function LDProvider({ children }: LDProviderProps) {
  const [LDComponent, setLDComponent] = useState<null | React.ComponentType<{
    children: ReactNode;
  }>>(null);
  const [loading, setLoading] = useState(true);

  // Function to get or create a unique anonymous user ID
  // This is used as the key when initializing the LaunchDarkly client
  const getOrCreateAnonymousId = () => {
    if (typeof window === 'undefined') {
      return '';
    }

    let userId = localStorage.getItem('userId');

    if (!userId) {
      const array = new Uint32Array(1);
      crypto.getRandomValues(array);
      const uniqueId = (array[0] ?? 0).toString(36) + Date.now().toString(36);
      userId = `user_${uniqueId}`; // Generate a short random ID
      localStorage.setItem('userId', userId);
    }

    return userId;
  };

  // Memoizes the userKey to ensure it remains stable across renders.
  const userKey = useMemo(() => getOrCreateAnonymousId(), []);

  useEffect(() => {
    // Initialize LaunchDarkly with your client-side ID and default user context
    async function initLD() {
      if (
        !LAUNCHDARKLY_CLIENT_ID ||
        typeof LAUNCHDARKLY_CLIENT_ID !== 'string'
      ) {
        setLoading(false);
        return;
      }

      try {
        const trackedKeys = ['bypass_get_started']; // Array of keys to track

        const Provider = await asyncWithLDProvider({
          clientSideID: LAUNCHDARKLY_CLIENT_ID,
          reactOptions: { useCamelCaseFlagKeys: true },
          context: {
            key: userKey,
            anonymous: true,
          },
          options: {
            inspectors: [
              {
                type: 'flag-used',
                name: 'dd-inspector',

                method: (key: string, detail: LDEvaluationDetail) => {
                  if (trackedKeys.includes(key)) {
                    datadogRum.addFeatureFlagEvaluation(key, detail.value);
                  }
                },
              },
            ],
          },
        });
        setLDComponent(() => Provider);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        console.error('Error: LaunchDarkly initialization failed:', error);
      }
    }
    if (userKey) {
      initLD();
    }
  }, []);

  if (loading)
    return (
      <CenteredContainer data-testid="simple-loader">
        <SimpleLoader color="green" />
      </CenteredContainer>
    );
  if (!LDComponent) return <>{children}</>;
  return <LDComponent>{children}</LDComponent>;
}
